import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDownIcon, Bars3Icon, XMarkIcon } from "@heroicons/react/24/outline";

const WebsiteLayout = ({ children, activeTab, setActiveTab }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navigationItems = [
    { id: 'platform', label: 'Platform', hasDropdown: false },
    { id: 'about', label: 'About Us', hasDropdown: false },
    { id: 'contact', label: 'Contact', hasDropdown: false },
    {
      id: 'framework',
      label: 'Framework',
      hasDropdown: true,
      dropdownItems: [
        { id: 'compliance-frameworks', label: 'Compliance Frameworks' },
        { id: 'global-privacy', label: 'Global Privacy' },
        { id: 'tprm', label: 'TPRM' }
      ]
    },
    { id: 'faq', label: 'FAQ', hasDropdown: false }
  ];

  const handleTabClick = (tabId) => {
    setActiveTab(tabId);
    setIsDropdownOpen(false);
    setIsMobileMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-cyan-900">
      {/* Fixed Navigation Header */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled 
          ? 'bg-slate-900/95 backdrop-blur-md border-b border-white/10 shadow-2xl' 
          : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between h-16">
            {/* Logo Removed */}

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              {navigationItems.map((item) => (
                <div key={item.id} className="relative">
                  {item.hasDropdown ? (
                    <div className="relative">
                      <button
                        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                        className={`flex items-center space-x-1 px-3 py-2 rounded-lg font-medium transition-all duration-200 ${
                          activeTab?.startsWith('framework') || isDropdownOpen
                            ? 'text-cyan-300 bg-white/10'
                            : 'text-white hover:text-cyan-300 hover:bg-white/5'
                        }`}
                      >
                        <span>{item.label}</span>
                        <ChevronDownIcon className={`w-4 h-4 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
                      </button>

                      <AnimatePresence>
                        {isDropdownOpen && (
                          <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            transition={{ duration: 0.2 }}
                            className="absolute top-full left-0 mt-2 w-56 bg-slate-800/95 backdrop-blur-md rounded-xl border border-white/10 shadow-2xl overflow-hidden"
                          >
                            {item.dropdownItems.map((dropdownItem) => (
                              <button
                                key={dropdownItem.id}
                                onClick={() => handleTabClick(dropdownItem.id)}
                                className={`w-full text-left px-4 py-3 text-sm font-medium transition-colors ${
                                  activeTab === dropdownItem.id
                                    ? 'text-cyan-300 bg-white/10'
                                    : 'text-white hover:text-cyan-300 hover:bg-white/5'
                                }`}
                              >
                                {dropdownItem.label}
                              </button>
                            ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ) : (
                    <button
                      onClick={() => handleTabClick(item.id)}
                      className={`px-3 py-2 rounded-lg font-medium transition-all duration-200 ${
                        activeTab === item.id
                          ? 'text-cyan-300 bg-white/10'
                          : 'text-white hover:text-cyan-300 hover:bg-white/5'
                      }`}
                    >
                      {item.label}
                    </button>
                  )}
                </div>
              ))}

              {/* CTA Button */}
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white px-6 py-2 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all"
                onClick={() => handleTabClick('contact')}
              >
                Get Started
              </motion.button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-2 rounded-lg text-white hover:bg-white/10 transition-colors"
            >
              {isMobileMenuOpen ? (
                <XMarkIcon className="w-6 h-6" />
              ) : (
                <Bars3Icon className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="md:hidden bg-slate-800/95 backdrop-blur-md border-t border-white/10"
            >
              <div className="max-w-7xl mx-auto px-6 py-4 space-y-2">
                {navigationItems.map((item) => (
                  <div key={item.id}>
                    {item.hasDropdown ? (
                      <>
                        <button
                          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                          className="flex items-center justify-between w-full px-3 py-2 text-left font-medium text-white hover:text-cyan-300 hover:bg-white/5 rounded-lg transition-colors"
                        >
                          <span>{item.label}</span>
                          <ChevronDownIcon className={`w-4 h-4 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
                        </button>
                        <AnimatePresence>
                          {isDropdownOpen && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: 'auto' }}
                              exit={{ opacity: 0, height: 0 }}
                              className="ml-4 space-y-1 overflow-hidden"
                            >
                              {item.dropdownItems.map((dropdownItem) => (
                                <button
                                  key={dropdownItem.id}
                                  onClick={() => handleTabClick(dropdownItem.id)}
                                  className={`block w-full text-left px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                                    activeTab === dropdownItem.id
                                      ? 'text-cyan-300 bg-white/10'
                                      : 'text-white hover:text-cyan-300 hover:bg-white/5'
                                  }`}
                                >
                                  {dropdownItem.label}
                                </button>
                              ))}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </>
                    ) : (
                      <button
                        onClick={() => handleTabClick(item.id)}
                        className={`block w-full text-left px-3 py-2 font-medium rounded-lg transition-colors ${
                          activeTab === item.id
                            ? 'text-cyan-300 bg-white/10'
                            : 'text-white hover:text-cyan-300 hover:bg-white/5'
                        }`}
                      >
                        {item.label}
                      </button>
                    )}
                  </div>
                ))}
                
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 text-white px-6 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all mt-4"
                  onClick={() => handleTabClick('contact')}
                >
                  Get Started
                </motion.button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      {/* Main Content */}
      <main className="pt-16">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-slate-900 border-t border-white/10 py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {/* Logo and Description */}
            <div className="col-span-1 md:col-span-2">
              <p className="text-gray-400 text-sm leading-relaxed max-w-md">
                AI-powered compliance automation platform helping businesses streamline regulatory adherence, 
                enhance data protection, and optimize risk management across 20+ frameworks.
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="text-white font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2">
                {navigationItems.filter(item => !item.hasDropdown).map((item) => (
                  <li key={item.id}>
                    <button
                      onClick={() => handleTabClick(item.id)}
                      className="text-gray-400 hover:text-cyan-300 text-sm transition-colors"
                    >
                      {item.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact Info */}
            <div>
              <h4 className="text-white font-semibold mb-4">Contact</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li>📧 enquiry@habilesec.com</li>
                <li>📞 +91-831-050-6586</li>
                <li>🎧 +91-435-290-0535</li>
                <li>📍 Kumbakonam, Tamil Nadu, India</li>
              </ul>
            </div>
          </div>

          <div className="border-t border-white/10 mt-8 pt-8 flex flex-col md:flex-row items-center justify-between">
            <p className="text-gray-400 text-sm">
              © 2025 HabileSec India Private Limited. All rights reserved.
            </p>
            <div className="flex space-x-6 mt-4 md:mt-0">
              <button className="text-gray-400 hover:text-cyan-300 text-sm transition-colors">
                Privacy Policy
              </button>
              <button className="text-gray-400 hover:text-cyan-300 text-sm transition-colors">
                Terms of Service
              </button>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default WebsiteLayout;